import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { CONSENT_LANGS } from "../../lib/helpers";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Field } from "../../components/Common";
import SignaturePad from "../../components/SignaturePad";
import PublicFrame, { usePublicBranding } from "./PublicFrame";

export default function ConsentSign({ token: tk, onDone }) {
  const { token: p } = useParams();
  const token = tk || p;
  const { t } = useT();
  const [data, setData] = useState(null);
  const [lang, setLang] = useState(null);
  const [name, setName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [sig, setSig] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/consent/${token}`, { params: { lang: lang || undefined } }).then((r) => {
      setData(r.data);
      setName((n) => n || r.data.client_name);
    }).catch((e) => setError(errMsg(e)));
  }, [token, lang, done]);
  usePublicBranding(data);

  const submit = async () => {
    setError("");
    try {
      await api.post(`/public/consent/${token}/sign`, { signer_name: name, signature: sig, agreed, language: data.language });
      setDone(true);
      onDone?.();
    } catch (e) { setError(errMsg(e)); }
  };
  if (!data) return <PublicFrame><p data-testid="consent-error">{error || "…"}</p></PublicFrame>;
  const signed = data.status === "signed";

  return (
    <PublicFrame branding={data.branding}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t("sign_form")}</p>
        {!signed && data.available_languages.length > 1 && (
          <div className="flex gap-1.5" data-testid="consent-lang-switch">
            {data.available_languages.map((l) => (
              <button key={l} data-testid={`consent-lang-${l}`} onClick={() => setLang(l)}
                className={`rounded-full border px-3 py-1 text-xs ${data.language === l ? "bg-primary text-primary-foreground" : ""}`}>{CONSENT_LANGS[l] || l}</button>
            ))}
          </div>
        )}
      </div>
      <h1 className="text-3xl font-bold mb-6" data-testid="consent-title">{data.title}</h1>
      <div className="rounded-[var(--radius)] border bg-card p-6 whitespace-pre-wrap text-sm leading-relaxed max-h-[50vh] overflow-y-auto" data-testid="consent-body">{data.body}</div>
      {signed || done ? (
        <div className="mt-6 flex items-center gap-3 rounded-[var(--radius)] border border-emerald-500/30 bg-emerald-500/10 p-5 text-emerald-800" data-testid="consent-signed-message">
          <CheckCircle2 className="h-5 w-5" />{t("thank_you_signed")}{data.signed_at && <span className="text-xs ml-auto">{data.signer_name} · {new Date(data.signed_at).toLocaleString()}</span>}
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="consent-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />{t("i_agree")}</label>
          <Field label={t("full_name")}><Input data-testid="consent-name-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <SignaturePad onChange={setSig} label={t("clear")} />
          {error && <p className="text-sm text-destructive" data-testid="consent-sign-error">{error}</p>}
          <Button data-testid="consent-submit-btn" className="w-full rounded-full h-12" disabled={!agreed || !sig || !name.trim()} onClick={submit}>{t("submit_sign")}</Button>
        </div>
      )}
    </PublicFrame>
  );
}
